/**
 * Sentence-end detection that ignores abbreviation/acronym periods so a
 * trailing acronym (e.g. "U.S.", "U.K.", "a.m.") isn't mistaken for the end
 * of a sentence — used by karaoke caption segmentation and SRT export to avoid
 * stranding the word that follows an abbreviation (e.g. "U.S." | "soccer.") on
 * its own line/cue.
 *
 * Recognizes ASCII terminators (`.!?`), the ellipsis, and CJK sentence enders
 * (`。！？`), allowing trailing quotes/brackets.
 *
 * Heuristic: text ending in a run of two or more single-letter + period groups
 * is an acronym, not a sentence boundary. This intentionally does not cover
 * single-word title abbreviations ("Dr.", "etc.") — only the acronym pattern.
 */
export function endsSentenceIgnoringAbbreviations(
  value: string | null,
): boolean {
  if (!value) return false;
  // Must end with sentence-final punctuation (allow trailing quotes/brackets).
  // Quotes: ASCII “ ‘ (U+22/27) plus curly variants (U+201c/2018/201d/2019).
  if (!/[.!?…。！？][\x22\x27“‘”’)\]]*\s*$/.test(value)) return false;
  // ...but not when that period closes an acronym like “U.S.” / “a.m.”.
  if (/(?:[A-Za-z]\.){2,}[\x22\x27“‘”’)\]]*\s*$/.test(value)) return false;
  return true;
}

/**
 * True when the text ends in an acronym pattern (“U.S.”, “U.K.”, “a.m.”) —
 * meaning the sentence continues past this point.  Used alongside
 * `endsSentenceIgnoringAbbreviations` to widen the merge-gap tolerance when
 * the orphaned segment is the abbreviation itself (not the word after it).
 */
export function endsWithAbbreviation(value: string | null): boolean {
  if (!value) return false;
  return /(?:[A-Za-z]\.){2,}[\x22\x27“‘”’)\]]*\s*$/.test(value);
}

/**
 * Compute word-overlap similarity between two strings.
 * Returns 0.0 (no overlap) to 1.0 (identical word sets).
 * Used to verify AI-returned text matches transcript content at given indices.
 */
export function wordOverlapSimilarity(a: string, b: string): number {
  const wordsA = a.toLowerCase().split(/\s+/).filter(Boolean);
  const wordsB = b.toLowerCase().split(/\s+/).filter(Boolean);
  if (wordsA.length === 0 || wordsB.length === 0) return 0;
  const setB = new Set(wordsB);
  const matches = wordsA.filter(w => setB.has(w)).length;
  return matches / Math.max(wordsA.length, wordsB.length);
}

export function equalsIgnoringCase(string1: string, string2: string) {
  return (
    string1.localeCompare(string2, undefined, { sensitivity: 'base' }) === 0
  );
}

export function removePunctuation(text: string) {
  // remove periods, commas, exclamation marks, question marks
  return text.replace(/[.,;!?]/g, '');
}

// Punctuation that attaches to the PRECEDING word — no space belongs before it.
// Single source of truth shared by `needsSpaceBetweenPhrases` (inline-render
// spacing) and `normalizeForCompare` (whitespace-insensitive comparison). The
// `]` is escaped for the character class; the rest are literal inside `[...]`.
// The CJK full-width and Arabic marks are here for the same reason as `…»”’`:
// a translated phrase's terminal punctuation is localised, so folding a
// punct-only phrase (`karaokeSegmentation.segmentTokensByPhrase`) or joining
// phrase values in prose has to recognise `。` exactly as it does `.`.
const NO_SPACE_BEFORE_PUNCTUATION =
  ',.;:!?)\\]}…»”’。、，．！？：；）」』〉》】〕〗〙〛〟］｝｠｣؟؛،';
const LEADING_NO_SPACE_PUNCTUATION = new RegExp(
  `^[${NO_SPACE_BEFORE_PUNCTUATION}]`,
);
// A single space directly before such punctuation (global; used only in
// `.replace`, so the `g`-flag `lastIndex` state never leaks into a `.test`).
const SPACE_BEFORE_NO_SPACE_PUNCTUATION = new RegExp(
  ` ([${NO_SPACE_BEFORE_PUNCTUATION}])`,
  'g',
);

// PLAT-11650 — the STORAGE-safe subset: the marks that NO locale spaces before,
// so stripping a leftover space is always a fix, never a corruption. This is
// deliberately NOT `NO_SPACE_BEFORE_PUNCTUATION`: that set is for comparison,
// where folding is symmetric and harmless, but `normalizeTypedTranslation`
// PERSISTS its output. It drops the ASCII high punctuation `;:!?`, the guillemet
// `»`, the closing quotes `”’`, and `…` — all of which FRENCH spaces before
// (`Bonjour !`, `« oui »`) and some of which English spaces around too. The
// CJK/fullwidth and Arabic marks stay: those scripts never put a space before
// them. Comma/period and the ASCII closing brackets are the cross-locale-safe
// core.
const NO_SPACE_BEFORE_PUNCTUATION_STORAGE = ',.)\\]}。、，！？：；）」』〉؟؛،';
const STORAGE_SPACE_BEFORE_PUNCTUATION = new RegExp(
  ` ([${NO_SPACE_BEFORE_PUNCTUATION_STORAGE}])`,
  'g',
);
// PLAT-11650 — a space directly after an opening bracket/quote that NO locale
// spaces after. `«` is excluded (French sets a space inside guillemets:
// `« oui »`); the ASCII brackets and English curly opening quotes stay, as do
// the Spanish inverted `¡`/`¿`, which open an exclamation/question and attach to
// the word (`¡Vaya!`, `¿Qué tal?`) — no locale spaces after them, so a stray one
// is always an artifact. (The matching close is ASCII `!`/`?`, whose PRECEDING
// space is left alone: French spaces before it, Spanish does not, and this
// normaliser cannot know which — that half needs the locale it does not have.)
const STORAGE_SPACE_AFTER_OPENING = /([([{“‘¡¿]) /g;
// PLAT-11650 — runs of BREAKING whitespace only. JS `\s` also matches the
// no-break (U+00A0) and NARROW no-break (U+202F) spaces that French sets before
// `;:!?`, inside `« »`, and as a thousands separator (`12 000`); folding those
// to an ordinary space is the first half of the corruption (wrong width AND now
// breakable), which the punctuation strip above then completed. `[^\S…]` is
// "whitespace, minus these two", so those two survive the collapse.
const BREAKING_WHITESPACE_RUN = /[^\S\u00A0\u202F]+/g;

/**
 * Whether a single space belongs between two adjacent translated phrase values
 * when rendering them inline. Translated phrase values carry their own interior
 * spacing/punctuation but NO inter-phrase whitespace, so the inline prose views
 * must insert it themselves. Yes by default; no before attaching/closing
 * punctuation, no right after an opening bracket/quote, no around a
 * whitespace-only value (which renders its own space via `pre-wrap`), and no
 * where both sides of the boundary are in a script that does not space its
 * words (PLAT-11636 — Japanese/Chinese prose joined with an ASCII space
 * rendered a stray gap mid-sentence in the transcript, the span editor and
 * the SRT export alike; Korean keeps the space because it spaces its words).
 */
export function needsSpaceBetweenPhrases(
  prevValue: string,
  currValue: string,
): boolean {
  const prev = prevValue ?? '';
  const curr = currValue ?? '';
  if (prev.trim() === '' || curr.trim() === '') return false;
  if (LEADING_NO_SPACE_PUNCTUATION.test(curr.trimStart())) return false;
  if (/[([{«“‘]$/.test(prev.trimEnd())) return false;
  return !isSpacelessBoundary(prev, curr);
}

// East Asian Wide / Fullwidth ranges (PLAT-11468): Hangul Jamo, CJK
// radicals + punctuation, kana, CJK ideographs (incl. Extension A and the
// supplementary planes), Hangul syllables, compatibility ideographs,
// vertical/compat forms, and Fullwidth Forms. Halfwidth katakana
// (FF61–FF9F) is deliberately narrow. One `u`-flag class so supplementary
// ideographs count as one (wide) character, not a surrogate pair.
const WIDE_CHAR =
  /[\u1100-\u115F\u2E80-\u303E\u3041-\u33FF\u3400-\u4DBF\u4E00-\u9FFF\uA000-\uA4CF\uAC00-\uD7A3\uF900-\uFAFF\uFE30-\uFE4F\uFF00-\uFF60\uFFE0-\uFFE6\u{1B000}-\u{1B2FF}\u{20000}-\u{3FFFD}]/u;

// PLAT-11636 — the subset of WIDE_CHAR whose scripts write NO space between
// words: CJK punctuation, kana, ideographs, compatibility/vertical forms and
// Fullwidth Forms. Hangul (Jamo, compatibility Jamo, syllables) is wide for
// width purposes but Korean spaces its words, so it is deliberately absent.
const SPACELESS_SCRIPT_CHAR =
  /[\u2E80-\u303E\u3041-\u312F\u3190-\u33FF\u3400-\u4DBF\u4E00-\u9FFF\uA000-\uA4CF\uF900-\uFAFF\uFE30-\uFE4F\uFF00-\uFF60\uFFE0-\uFFE6\u{1B000}-\u{1B2FF}\u{20000}-\u{3FFFD}]/u;

// Fullwidth CJK punctuation (、。「」（）！？：… and the Fullwidth Forms block's
// marks — not its letters/digits). These glyphs carry their own visual
// spacing, so nothing is spaced after them, Latin included (`として、Toro`).
const CJK_PUNCTUATION =
  /[\u3001-\u303F\uFF01-\uFF0F\uFF1A-\uFF20\uFF3B-\uFF40\uFF5B-\uFF65]/u;

// PLAT-11836 — fullwidth opening brackets/quotes (「『（【 …): these attach to
// the character AFTER them, the mirror of NO_SPACE_BEFORE_PUNCTUATION.
const CJK_OPENING_PUNCTUATION = /[〈《「『【〔〖〘〚〝（［｛｟｢]/u;
const ONLY_CJK_OPENING_PUNCTUATION = /^[〈《「『【〔〖〘〚〝（［｛｟｢]+$/u;

/** PLAT-11836 — `text` is nothing but fullwidth opening brackets (`「`, `【「`). */
export function isCjkOpeningPunctuation(text: string): boolean {
  return ONLY_CJK_OPENING_PUNCTUATION.test(text);
}

/**
 * PLAT-11836 — split one spaceless-script word into the units a caption
 * cursor may stop between. Japanese has no spaces, so the word tokenizer's
 * dictionary "words" are the only slots the caption editor exposed, and a
 * user could not break a line between two characters the dictionary happened
 * to join. Every character of a spaceless script becomes its own unit; a run
 * of other characters inside the word (Latin, digits) stays one unit;
 * attaching punctuation (`。、」`) rides on the unit before it and opening
 * brackets (`「（`) on the unit after, so neither ever leads or trails a line
 * alone. Words with no spaceless-script character come back as `[word]`.
 * The units concatenate back to `word` exactly.
 */
export function splitSpacelessScriptCharacters(word: string): string[] {
  if (!SPACELESS_SCRIPT_CHAR.test(word)) return [word];
  const units: string[] = [];
  let pending = ''; // an opening bracket waiting for its character
  let run = ''; // a run of non-spaceless characters
  const push = (unit: string) => {
    units.push(pending + unit);
    pending = '';
  };
  for (const char of word) {
    // CJK_PUNCTUATION also covers the halfwidth brackets ｢｣, which are narrow
    // and so outside SPACELESS_SCRIPT_CHAR.
    if (!SPACELESS_SCRIPT_CHAR.test(char) && !CJK_PUNCTUATION.test(char)) {
      run += char;
      continue;
    }
    if (run) {
      push(run);
      run = '';
    }
    if (CJK_OPENING_PUNCTUATION.test(char)) {
      pending += char;
    } else if (LEADING_NO_SPACE_PUNCTUATION.test(char) && units.length > 0) {
      units[units.length - 1] += char;
    } else {
      push(char);
    }
  }
  if (run || pending) push(run);
  return units;
}

/**
 * PLAT-11636 — whether the boundary between `prev` and `curr` needs no
 * separator because it sits inside spaceless-script prose: both sides in a
 * spaceless script (see SPACELESS_SCRIPT_CHAR), or `prev` ending in fullwidth
 * punctuation. Mixed word boundaries (`Dingo` / `実現`) keep the space, as
 * does anything Korean.
 */
function isSpacelessBoundary(prev: string, curr: string): boolean {
  const prevLast = [...prev.trimEnd()].pop();
  const nextFirst = [...curr.trimStart()][0];
  if (!prevLast || !nextFirst) return false;
  if (CJK_PUNCTUATION.test(prevLast)) return true;
  return (
    SPACELESS_SCRIPT_CHAR.test(prevLast) &&
    SPACELESS_SCRIPT_CHAR.test(nextFirst)
  );
}

/**
 * PLAT-11468 — a string's width in Latin character units: full-width (CJK)
 * characters count 2, everything else 1. Caption line-length limits are
 * calibrated in Latin widths, and counting code units instead made Japanese
 * lines render ~2× the intended width (with no whitespace for the renderer
 * to wrap at). Iterates code points, so supplementary ideographs count once.
 */
export function visualTextWidth(text: string): number {
  let width = 0;
  for (const ch of text) {
    width += WIDE_CHAR.test(ch) ? 2 : 1;
  }
  return width;
}

/**
 * PLAT-11468 — whether the text contains any full-width (CJK) character.
 * Full-width runs carry no whitespace, so the renderer cannot wrap them —
 * segmentation uses this to know when a line must be guaranteed to fit.
 */
export function containsWideChar(text: string): boolean {
  return WIDE_CHAR.test(text);
}

/**
 * PLAT-11468 — caption-row joiner. Originally `needsSpaceBetweenPhrases` minus
 * the space at a full-width boundary; since PLAT-11636 the phrase predicate
 * itself knows spaceless scripts (and, unlike the old wide-char check here,
 * leaves Korean's spaces alone), so this is the same rule under the name the
 * karaoke producer uses.
 */
export function needsSpaceBetweenCaptionRows(
  prevValue: string,
  currValue: string,
): boolean {
  return needsSpaceBetweenPhrases(prevValue, currValue);
}

/**
 * Join translated phrase values into one string with the same punctuation-aware
 * spacing `needsSpaceBetweenPhrases` applies inline, so workspace block strings
 * match the inline prose (no space before commas/periods).
 */
export function joinPhraseValues(values: readonly string[]): string {
  let out = '';
  for (let i = 0; i < values.length; i++) {
    const v = values[i] ?? '';
    if (i > 0 && needsSpaceBetweenPhrases(values[i - 1] ?? '', v)) out += ' ';
    out += v;
  }
  return out;
}

/**
 * Normalize a string for whitespace-INSENSITIVE comparison only. Collapses every
 * whitespace run (including newlines) to a single space, drops any space directly
 * before a no-space-before punctuation character (the SAME set
 * `needsSpaceBetweenPhrases` treats as attaching to the previous word), and trims
 * both ends. This mirrors the data model, where inter-word spacing is
 * reconstructed at render rather than stored as content, so two values that render
 * identically compare equal (e.g. "над ними . И вот " === "над ними. И вот").
 *
 * Comparison-only: NEVER use the result to mutate a stored value — change
 * detection compares normalized forms but always stores the user's raw value.
 */
export function normalizeForCompare(s: string): string {
  return (s ?? '')
    .replace(/\s+/g, ' ')
    .replace(SPACE_BEFORE_NO_SPACE_PUNCTUATION, '$1')
    .trim();
}

/**
 * PLAT-11650 — tidy text a reviewer TYPED into a translated phrase, at commit.
 * The one normaliser that may produce a stored value (unlike
 * `normalizeForCompare`, which is comparison-only), so it must never corrupt
 * locale-specific typography: it collapses runs of BREAKING whitespace to one
 * space while PRESERVING no-break (U+00A0) and narrow no-break (U+202F) spaces,
 * drops a leftover space only before cross-locale-safe attaching punctuation
 * (comma/period, closing brackets, CJK/Arabic marks — never `;:!?«»`, which
 * French spaces) and after an opening bracket/quote (never `«`; includes the
 * Spanish inverted `¡`/`¿`), then trims. It never INSERTS a space — `a.b`,
 * decimals, abbreviations and space-less CJK pass through untouched — so
 * deleting a word from "I really, like you" yields "I really, you", while
 * French `Bonjour !` / `« d'accord »` and Spanish `¿Qué tal?` are kept correct.
 */
export function normalizeTypedTranslation(value: string): string {
  return (value ?? '')
    .replace(BREAKING_WHITESPACE_RUN, ' ')
    .replace(STORAGE_SPACE_BEFORE_PUNCTUATION, '$1')
    .replace(STORAGE_SPACE_AFTER_OPENING, '$1')
    .trim();
}

export const extractTitleFromContent = (
  contentHtml: string,
  maxWords: number = 5,
): string => {
  if (!contentHtml) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(contentHtml, 'text/html');

  // First try to get title from H1 or H2 tags
  const h1 = doc.querySelector('h1')?.textContent?.trim();
  const h2 = doc.querySelector('h2')?.textContent?.trim();

  let titleText = h1 || h2;

  // If no heading found, extract from the first paragraph or body text
  if (!titleText) {
    const firstParagraph = doc.querySelector('p')?.textContent?.trim();
    const bodyText = doc.body?.textContent?.trim();
    titleText = firstParagraph || bodyText || '';
  }

  if (!titleText) return '';

  // Clean up the text and extract first few words
  const words = titleText
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim()
    .split(' ')
    .slice(0, maxWords)
    .join(' ');

  // Remove trailing punctuation if present
  return words.replace(/[.,;!?]+$/, '');
};
